import { useEffect, useState } from "react";
import { Link, useLocation } from "react-router-dom";
import { motion, AnimatePresence } from "framer-motion";
import { Sparkles, Loader2, RefreshCw, Moon } from "lucide-react";
import { toast } from "sonner";
import Masthead from "@/components/alcott/Masthead";
import Seo from "@/components/Seo";
import AlcottFooter from "@/components/alcott/AlcottFooter";
import HorizonWaitlist from "@/components/alcott/HorizonWaitlist";
import { drawArchetype } from "@/lib/api";
import { SECRETS_ESSAYS, SECRETS_IMG, SACRED_SCROLLS } from "@/data/alcott";

const ease = [0.22, 1, 0.36, 1];
const DRAW_KEY = "sybils_secrets_daily_draw";
const today = () => new Date().toISOString().slice(0, 10);

const scrollToOracle = () => {
  const el = document.getElementById("oracle");
  if (el) el.scrollIntoView({ behavior: "smooth" });
};

// --- The daily archetype-card oracle ---------------------------------------
const OracleDraw = () => {
  const [name, setName] = useState("");
  const [intention, setIntention] = useState("");
  const [loading, setLoading] = useState(false);
  const [card, setCard] = useState(null);

  useEffect(() => {
    try {
      const saved = JSON.parse(localStorage.getItem(DRAW_KEY) || "null");
      if (saved && saved.day === today() && saved.card) setCard(saved.card);
    } catch (e) {
      /* ignore */
    }
  }, []);

  const draw = async () => {
    setLoading(true);
    try {
      const res = await drawArchetype({
        name: name.trim() || undefined,
        intention: intention.trim() || undefined,
      });
      setCard(res);
      localStorage.setItem(DRAW_KEY, JSON.stringify({ day: today(), card: res }));
    } catch (err) {
      toast.error("The Oracle is momentarily veiled. Try again in a moment.");
    } finally {
      setLoading(false);
    }
  };

  return (
    <section
      id="oracle"
      className="relative overflow-hidden bg-[#0A0A0A] px-5 py-20 text-[#FAF9F6] md:px-10 md:py-28"
      data-testid="secrets-oracle"
    >
      <div
        className="pointer-events-none absolute inset-0 opacity-40"
        style={{
          backgroundImage:
            "radial-gradient(1px 1px at 20% 30%, rgba(255,255,255,0.5) 50%, transparent), radial-gradient(1px 1px at 70% 60%, rgba(255,255,255,0.35) 50%, transparent), radial-gradient(1.5px 1.5px at 45% 80%, rgba(192,92,66,0.6) 50%, transparent), radial-gradient(1px 1px at 85% 20%, rgba(255,255,255,0.4) 50%, transparent)",
        }}
      />
      <div className="relative mx-auto max-w-[1100px]">
        <div className="text-center">
          <p className="font-body text-[0.7rem] uppercase tracking-[0.3em] text-[#C05C42]">Consult the Oracle</p>
          <h2 className="mt-4 font-serif text-[clamp(2.2rem,5vw,4rem)] font-semibold leading-[1.02] tracking-tight">
            Draw Your Daily Card
          </h2>
          <p className="mx-auto mt-5 max-w-xl font-body text-base leading-relaxed text-[#FAF9F6]/60">
            One archetype rises for you each day — a matriarchal mirror and a statement of power.
            Offer your name and intention, and let the Oracle speak.
          </p>
        </div>

        <div className="mx-auto mt-12 grid max-w-4xl grid-cols-1 gap-10 md:grid-cols-2 md:items-center">
          {/* Form */}
          <div className="space-y-4" data-testid="secrets-oracle-form">
            <input
              value={name}
              onChange={(e) => setName(e.target.value)}
              placeholder="Your name (optional)"
              className="w-full rounded-xl border border-[#FAF9F6]/15 bg-[#FAF9F6]/[0.04] px-4 py-3.5 font-body text-sm text-[#FAF9F6] outline-none placeholder:text-[#FAF9F6]/35 focus:border-[#C05C42]"
              data-testid="secrets-oracle-name"
            />
            <textarea
              value={intention}
              onChange={(e) => setIntention(e.target.value)}
              placeholder="Your intention for today (optional)"
              rows={3}
              className="w-full resize-none rounded-xl border border-[#FAF9F6]/15 bg-[#FAF9F6]/[0.04] px-4 py-3.5 font-body text-sm text-[#FAF9F6] outline-none placeholder:text-[#FAF9F6]/35 focus:border-[#C05C42]"
              data-testid="secrets-oracle-intention"
            />
            <button
              onClick={draw}
              disabled={loading}
              className="inline-flex w-full items-center justify-center gap-2 rounded-full bg-[#C05C42] px-6 py-4 font-body text-[0.7rem] uppercase tracking-[0.22em] text-[#FAF9F6] transition-transform duration-300 hover:-translate-y-0.5 disabled:opacity-60"
              data-testid="secrets-oracle-draw-btn"
            >
              {loading ? <Loader2 size={15} className="animate-spin" /> : <Sparkles size={15} />}
              {card ? "Draw again" : "Draw your card"}
            </button>
            {card && (
              <button
                onClick={draw}
                disabled={loading}
                className="inline-flex items-center gap-2 font-body text-[0.65rem] uppercase tracking-[0.2em] text-[#FAF9F6]/45 transition-colors hover:text-[#C05C42]"
                data-testid="secrets-oracle-redraw"
              >
                <RefreshCw size={12} /> A different mirror
              </button>
            )}
          </div>

          {/* Card */}
          <div className="min-h-[360px]">
            <AnimatePresence mode="wait">
              {card ? (
                <motion.div
                  key={card.archetype + (card.statement || "")}
                  initial={{ opacity: 0, rotateY: 90 }}
                  animate={{ opacity: 1, rotateY: 0 }}
                  exit={{ opacity: 0, rotateY: -90 }}
                  transition={{ duration: 0.7, ease }}
                  className="overflow-hidden rounded-2xl border border-[#C05C42]/40 bg-[#FAF9F6]/[0.03]"
                  data-testid="secrets-oracle-card"
                >
                  <div className="relative aspect-[16/10] overflow-hidden">
                    <img src={card.image} alt={card.name} className="h-full w-full object-cover" />
                    <div className="absolute inset-0 bg-gradient-to-t from-[#0A0A0A] via-transparent to-transparent" />
                    <div className="absolute bottom-4 left-5">
                      <p className="font-body text-[0.6rem] uppercase tracking-[0.25em] text-[#C05C42]">
                        {card.element}
                      </p>
                      <p className="font-serif text-2xl font-semibold" data-testid="secrets-oracle-card-name">
                        {card.name}
                      </p>
                      <p className="font-body text-[0.7rem] uppercase tracking-[0.14em] text-[#FAF9F6]/60">
                        {card.title}
                      </p>
                    </div>
                  </div>
                  <div className="p-6">
                    <p className="font-serif text-lg italic leading-relaxed text-[#F4EFE6]" data-testid="secrets-oracle-card-statement">
                      {card.statement}
                    </p>
                  </div>
                </motion.div>
              ) : (
                <motion.div
                  key="placeholder"
                  initial={{ opacity: 0 }}
                  animate={{ opacity: 1 }}
                  className="flex aspect-[16/10] flex-col items-center justify-center rounded-2xl border border-dashed border-[#FAF9F6]/20 text-center"
                >
                  <Moon size={30} strokeWidth={1.2} className="text-[#C05C42]" />
                  <p className="mt-4 max-w-xs px-6 font-body text-sm text-[#FAF9F6]/45">
                    Your card of the day waits unseen. Draw to reveal the archetype rising for you.
                  </p>
                </motion.div>
              )}
            </AnimatePresence>
          </div>
        </div>

        <p className="mx-auto mt-10 max-w-lg text-center font-body text-xs leading-relaxed text-[#FAF9F6]/40">
          For your full birth chart, daily fortune, and the counsel of ancestral traditions,
          the deeper Oracle awaits within{" "}
          <Link to="/veil" onClick={() => sessionStorage.removeItem("sybil_entered")} className="text-[#C05C42] hover:underline" data-testid="secrets-sanctum-link">
            the Inner Sanctum of Sybils Society
          </Link>
          .
        </p>
      </div>
    </section>
  );
};

// --- Editorial grid ---------------------------------------------------------
const EssayCard = ({ a, i }) => (
  <motion.div
    initial={{ opacity: 0, y: 28 }}
    whileInView={{ opacity: 1, y: 0 }}
    viewport={{ once: true, margin: "-8%" }}
    transition={{ duration: 0.7, delay: (i % 3) * 0.06, ease }}
    data-testid={`secrets-essay-${a.id}`}
  >
    <Link to={`/essay/${a.slug}`} className="group block">
      <div className="relative aspect-[4/5] overflow-hidden rounded-sm">
        <img src={a.image} alt={a.title} className="h-full w-full object-cover transition-transform duration-[900ms] group-hover:scale-105" />
      </div>
      <p className="mt-5 font-body text-[0.62rem] uppercase tracking-[0.22em] text-[#C05C42]">{a.category}</p>
      <h3 className="mt-2 font-serif text-2xl font-semibold leading-tight tracking-tight">{a.title}</h3>
      <p className="mt-2 font-body text-sm leading-relaxed text-[#0A0A0A]/60">{a.excerpt}</p>
      <p className="mt-3 font-body text-[0.62rem] uppercase tracking-[0.18em] text-[#0A0A0A]/40">
        {a.author} · {a.readTime}
      </p>
    </Link>
  </motion.div>
);

export const SybilsSecrets = () => {
  const [waitlist, setWaitlist] = useState(false);
  const { hash } = useLocation();

  useEffect(() => {
    if (!hash) return;
    const id = hash.replace("#", "");
    const scroll = () => {
      const el = document.getElementById(id);
      if (!el) return;
      if (window.lenis) window.lenis.scrollTo(el, { offset: -110 });
      else el.scrollIntoView({ behavior: "smooth" });
    };
    const t1 = setTimeout(scroll, 450);
    const t2 = setTimeout(scroll, 1300);
    return () => {
      clearTimeout(t1);
      clearTimeout(t2);
    };
  }, [hash]);

  return (
    <div className="min-h-screen bg-[#FAF9F6] font-body text-[#0A0A0A]" data-testid="secrets-page">
      <Seo title="Sybils Secrets" description="Lifestyle & Esoterica — essays on matriarchal ritual, lunar living, and the well-kept self, with a live daily oracle from Sybils & Society." />
      <Masthead />

      {/* Hero */}
      <section className="relative overflow-hidden bg-[#0A0A0A] text-[#FAF9F6]" data-testid="secrets-hero">
        <div className="mx-auto grid max-w-[1500px] grid-cols-1 lg:grid-cols-2">
          <div className="order-2 flex flex-col justify-center px-5 py-16 md:px-10 md:py-28 lg:order-1">
            <motion.div initial={{ opacity: 0, y: 24 }} animate={{ opacity: 1, y: 0 }} transition={{ duration: 0.9, ease }}>
              <p className="font-body text-[0.7rem] uppercase tracking-[0.3em] text-[#C05C42]">Lifestyle & Esoterica</p>
              <h1 className="mt-5 font-serif text-[clamp(2.6rem,6.5vw,5rem)] font-semibold leading-[0.96] tracking-tight">
                Sybils Secrets
              </h1>
              <p className="mt-6 max-w-lg font-body text-lg leading-relaxed text-[#FAF9F6]/65">
                The matriarchal art of living by the sky — lunar living, ancestral astronomy, and the
                small rituals of a sovereign life. Draw your daily card and read your own heavens.
              </p>
              <div className="mt-9 flex flex-wrap gap-4">
                <button
                  onClick={scrollToOracle}
                  className="inline-flex items-center gap-2 rounded-full bg-[#C05C42] px-7 py-3.5 font-body text-[0.65rem] uppercase tracking-[0.2em] text-[#FAF9F6] transition-transform duration-300 hover:-translate-y-0.5"
                  data-testid="secrets-hero-draw-btn"
                >
                  <Sparkles size={14} /> Draw your daily card
                </button>
              </div>
            </motion.div>
          </div>
          <div className="order-1 h-[46vh] overflow-hidden lg:order-2 lg:h-auto">
            <img src={SECRETS_IMG.hero} alt="Sybils Secrets" className="h-full w-full object-cover" />
          </div>
        </div>
      </section>

      {/* Oracle */}
      <OracleDraw />

      {/* Editorial */}
      <section className="mx-auto max-w-[1500px] px-5 py-20 md:px-10 md:py-28" data-testid="secrets-editorial">
        <div className="mb-12">
          <p className="font-body text-[0.62rem] uppercase tracking-[0.25em] text-[#C05C42]">The Almanac</p>
          <h2 className="mt-3 font-serif text-[clamp(1.9rem,4vw,3rem)] font-semibold tracking-tight">
            Living by the Sky
          </h2>
          <p className="mt-3 max-w-2xl font-body text-sm leading-relaxed text-[#0A0A0A]/55">
            Essays on matriarchal astrology and the lifestyle of ritual — by Veronica Bey.
          </p>
        </div>
        <div className="grid grid-cols-1 gap-x-8 gap-y-14 sm:grid-cols-2 lg:grid-cols-3">
          {SECRETS_ESSAYS.map((a, i) => (
            <EssayCard key={a.id} a={a} i={i} />
          ))}
        </div>
      </section>

      {/* Sacred Scrolls — reading room */}
      <section id="sacred-scrolls" className="scroll-mt-28 border-t border-[#0A0A0A]/12 bg-[#F4EFE6]" data-testid="secrets-sacred-scrolls">
        <div className="mx-auto max-w-[1500px] px-5 py-20 md:px-10 md:py-28">
          <div className="mb-12">
            <p className="font-body text-[0.62rem] uppercase tracking-[0.25em] text-[#C05C42]">Sacred Scrolls · Books</p>
            <h2 className="mt-3 font-serif text-[clamp(1.9rem,4vw,3rem)] font-semibold tracking-tight">
              The Reading Room
            </h2>
            <p className="mt-3 max-w-2xl font-body text-sm leading-relaxed text-[#0A0A0A]/55">
              Book reviews of old, out-of-print magical texts — grimoires and folk-magic classics —
              with links to full scans and PDF downloads. Academic, and a little bewitched.
            </p>
          </div>
          <div className="grid grid-cols-1 gap-x-8 gap-y-14 sm:grid-cols-2 lg:grid-cols-3">
            {SACRED_SCROLLS.map((a, i) => (
              <EssayCard key={a.id} a={a} i={i} />
            ))}
          </div>
        </div>
      </section>

      <AlcottFooter onJoin={() => setWaitlist(true)} />
      <HorizonWaitlist open={waitlist} onClose={() => setWaitlist(false)} variant="outer" />
    </div>
  );
};

export default SybilsSecrets;
